import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { MapLayerCategoryEnum } from '../../models/map-layer-category.enum';
import { MapLayerDefinition } from '../../models/map-layer-definition.model';
import { MapLayerGroup } from '../../models/map-layer-group.model';
import { MapLayerPresetType } from '../../models/map-layer-preset.type';

@Component({
  selector: 'app-map-layers-panel',
  templateUrl: './map-layers-panel.component.html',
  styleUrls: ['./map-layers-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapLayersPanelComponent {
  @Input() visible = false;
  @Input() layerGroups: MapLayerGroup[] = [];

  @Output() closed = new EventEmitter<void>();
  @Output() overlayVisibilityChanged = new EventEmitter<{
    layerId: string;
    visible: boolean;
  }>();
  @Output() baseLayerChanged = new EventEmitter<string>();
  @Output() presetSelected = new EventEmitter<MapLayerPresetType>();
  @Output() showAllClicked = new EventEmitter<void>();
  @Output() hideAllClicked = new EventEmitter<void>();
  @Output() resetClicked = new EventEmitter<void>();

  readonly expandedCategories = new Set<MapLayerCategoryEnum>();
  readonly MapLayerCategoryEnum = MapLayerCategoryEnum;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.visible) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const clickedInsidePanel = this.elementRef.nativeElement.contains(target);
    const clickedLayersFab = !!target.closest('.map-layers-fab');

    if (!clickedInsidePanel && !clickedLayersFab) {
      this.closed.emit();
    }
  }

  isExpanded(category: MapLayerCategoryEnum): boolean {
    return this.expandedCategories.has(category);
  }

  toggleGroup(category: MapLayerCategoryEnum): void {
    if (this.expandedCategories.has(category)) {
      this.expandedCategories.delete(category);
      return;
    }

    this.expandedCategories.add(category);
  }

  onOverlayToggle(layer: MapLayerDefinition, event: CustomEvent): void {
    this.overlayVisibilityChanged.emit({
      layerId: layer.id,
      visible: !!event?.detail?.checked,
    });
  }

  onBaseLayerSelected(layerId: string): void {
    this.baseLayerChanged.emit(layerId);
  }

  onSelectGroup(category: MapLayerCategoryEnum): void {
    this.expandedCategories.clear();
    this.expandedCategories.add(category);

    switch (category) {
      case MapLayerCategoryEnum.COORDINATION_AREAS:
      case MapLayerCategoryEnum.INFRASTRUCTURE:
        this.presetSelected.emit('infrastructure');
        break;
      case MapLayerCategoryEnum.OPERATIONS:
        this.presetSelected.emit('operations');
        break;
      case MapLayerCategoryEnum.POLICE_AREAS:
        this.presetSelected.emit('police');
        break;
      case MapLayerCategoryEnum.RELAY:
        this.presetSelected.emit('relay');
        break;
      case MapLayerCategoryEnum.DEBUG:
        this.presetSelected.emit('debug');
        break;
      default:
        break;
    }
  }

  /** Returns true when the group should be rendered in the panel. */
  isGroupVisible(group: MapLayerGroup): boolean {
    return !group.visibleIf || group.visibleIf();
  }

  get baseLayers(): MapLayerDefinition[] {
    const baseGroup = this.layerGroups.find(
      (group) => group.category === MapLayerCategoryEnum.BASE
    );
    return baseGroup?.layers ?? [];
  }

  get overlayGroups(): MapLayerGroup[] {
    return this.layerGroups.filter(
      (group) => group.category !== MapLayerCategoryEnum.BASE
    );
  }

  get selectedBaseLayerId(): string | null {
    const selectedLayer = this.baseLayers.find((layer) => layer.visible);
    return selectedLayer ? selectedLayer.id : null;
  }

  trackByGroup(index: number, group: MapLayerGroup): string {
    return group.category;
  }

  trackByLayer(index: number, layer: MapLayerDefinition): string {
    return layer.id;
  }
}
